// Shared popup-positioning helper for the three raw Fluent `Combobox`/`Dropdown`
// popups that open inside a Dialog: `EntityPicker` (this directory), the
// entity-type `Dropdown` in `AssociatedEntitiesEditor.tsx`, and the freeform
// vehicle-field `Combobox` in `screens/TrainingScreen.tsx`. (A fourth caller —
// the "Path to Point Click Care" `Dropdown` in `EditableInvoiceForm.tsx` — was
// replaced by a plain Button, owner item 5, 2026-08-12.) All three hit the
// same failure — `autoSize` collapsing the popup to one scrolling row and
// flipping it above the field — for the same reason (app/README.md's Fluent
// UI v9 gotcha list).
// A plain function, not a component: `react-refresh/only-export-components`
// refuses to share a file's fast-refresh boundary between a component and a
// runtime function export, which is why this lives beside `EntityPicker.tsx`
// rather than inside it.

import { makeStyles } from '@fluentui/react-components'

/**
 * `positioning` is carried UNCONDITIONALLY: it fixes the collapse whether the
 * popup ends up portaled at a resolved dialog host or, lacking one, inline —
 * measuring against the window and turning `autoSize` off is what the inline
 * fallback needs too, not only the portaled case. The host/inline choice is
 * the other, separate half, and it is mutually exclusive: a resolved host
 * mounts the popup at the dialog surface (inside the focus trap, above the
 * fields it would otherwise fight for stacking order with); no host falls
 * back to `inlinePopup`, which is the FALLBACK now, not the norm — most
 * callers resolve a host and never take this branch.
 */
export function popupPositionProps(host: HTMLElement | null | undefined) {
  return {
    ...(host ? { mountNode: host } : { inlinePopup: true as const }),
    positioning: {
      position: 'below' as const,
      align: 'start' as const,
      autoSize: false,
      flipBoundary: 'window' as const,
      overflowBoundary: 'window' as const,
    },
  }
}

const useListboxStyles = makeStyles({
  // Two failures this fixes for the two callers below, neither of which
  // styles its own popup the way `EntityPicker`'s `s.listbox` does:
  //
  // 1. STACKING. `usePositioningOptions` resolves `position: 'below'` (above)
  //    to `position: absolute`, and Fluent's `useListboxStyles` sets no
  //    z-index of its own. An explicit `mountNode` (what `popupPositionProps`
  //    passes whenever `host` resolves) disables `usePortal`'s OWN mount-node
  //    creation, and `usePortalMountNodeStyles` — the only place Fluent sets
  //    `zIndex: 1000000` — fires only for that self-supplied node. So a
  //    listbox mounted at a resolved dialog host lands in the z-auto layer of
  //    that dialog's stacking context: a sibling, not a superior, of anything
  //    else raised in the same dialog (a `PopoverSurface` with its own
  //    explicit z-index, a sticky action bar). `AssociatedEntitiesEditor`'s
  //    type `Dropdown` is the deterministic case — it opens inside a
  //    `PopoverSurface` at `zIndex: 10` (`surface` in
  //    `LineItemIconButton.tsx`) mounted at the same dialog host, so without
  //    this it renders behind its own popover.
  //
  // 2. HEIGHT. `autoSize: false` (in `positioning` above) means Fluent writes
  //    no inline `max-height` — the fix for the collapse this file exists
  //    for, but it also means nothing bounds the popup's height any more.
  //    Before this shared helper existed both callers ran `inlinePopup`
  //    with Fluent's default positioning (`autoSize` true, which DOES cap
  //    them), so this height was never missing until `autoSize` turned off.
  //    Worst case: `TrainingScreen`'s freeform vehicle-field suggestions are a
  //    Set over the entire fleet — `company_label` alone can run to several
  //    dozen rows, uncapped and unscrolled without this.
  //
  // 3. WIDTH (D-351). Fluent sizes the list to its field, and Rules → Routing's
  //    condition value field is about 100px wide, so the vendor list opened as
  //    a strip with names broken mid-word and a sideways scrollbar. A floor of
  //    280px keeps a name and its ID line readable; a wider field still sets
  //    the width. A single word longer than that wraps rather than scrolling.
  popupListbox: {
    zIndex: 1000,
    maxHeight: '280px',
    overflowY: 'auto',
    minWidth: '280px',
    overflowWrap: 'anywhere',
  },
})

/**
 * The listbox class the two non-`EntityPicker` callers of
 * `popupPositionProps` need — `EntityPicker` carries the same two rules on its
 * own `s.listbox` (plus the surface/legibility styling that one already
 * needed) and is unaffected by this. Call as a hook in the caller's render and
 * pass the result through `listbox={{ className }}` alongside
 * `{...popupPositionProps(host)}`.
 */
export function usePopupListboxClass(): string {
  return useListboxStyles().popupListbox
}

/**
 * A POP-UP OR TOOLTIP THAT STAYS INSIDE THE INVOICE MODAL (QA C1-09,
 * 2026-09-25). The line-item icons — Additional Detail and Associated
 * Entities — sit at the right end of each row, and their 540px pop-ups used
 * Fluent's default placement (below, centred on the icon, bounded by the
 * window), so they ran past the modal's right edge, over its scrollbar, to
 * the edge of the screen. The disabled Mark Resolved tooltip did the same.
 *
 * `align: 'end'` (the default) lines the pop-up's right edge up with the
 * trigger's, so it opens leftward, into the modal; a tooltip on a field that
 * is not at the right edge keeps its own `align`. The modal's own surface (`host`, the
 * nearest `[role="dialog"]`) is the boundary for both the flip and the
 * overflow shift, so a pop-up near the bottom opens upward and one that would
 * still overhang is pushed back inside, 8px from the edge. With no dialog
 * (a queue card) the window is the boundary, which is Fluent's own default.
 */
export function withinDialogPositioning(
  host: HTMLElement | null | undefined,
  { position = 'below', align = 'end' }: { position?: 'below' | 'above'; align?: 'start' | 'center' | 'end' } = {},
) {
  return {
    position,
    align,
    ...(host ? { overflowBoundary: host, flipBoundary: host } : {}),
    overflowBoundaryPadding: 8,
  }
}
